import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import type { QuizResult } from '@/components/education/QuizResultsTable'

// ─── Types ────────────────────────────────────────────────────────────────────
export interface UniversityProgressData {
    quizResults: QuizResult[]
    degreeNames: string[]
    totalQuizzes: number
    averageScore: number
    bestScore: number
    weakAreas: string[]
    loading: boolean
    error: string | null
    refetch: () => void
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useUniversityProgress(): UniversityProgressData {
    const { user } = useAuth()
    const [quizResults, setQuizResults] = useState<QuizResult[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const fetchData = useCallback(async () => {
        if (!user) return
        setLoading(true)
        setError(null)

        try {
            // Fetch quiz results with class names
            const { data: results, error: qErr } = await supabase
                .from('education_quiz_results')
                .select(`
          id, progress_id, user_id, class_id, category_id,
          lesson_unit, lesson_number, questions,
          score_percentage, duration_seconds, attempted_at,
          classes (name)
        `)
                .eq('user_id', user.id)
                .order('attempted_at', { ascending: false })
                .limit(100)

            if (qErr) throw qErr

            const mapped: QuizResult[] = (results || []).map(r => {
                const cls = Array.isArray(r.classes) ? r.classes[0] : r.classes
                return {
                    id: r.id,
                    progress_id: r.progress_id,
                    user_id: r.user_id,
                    class_id: r.class_id,
                    category_id: r.category_id,
                    lesson_unit: r.lesson_unit,
                    lesson_number: r.lesson_number,
                    questions: (r.questions as any) || [],
                    score_percentage: Number(r.score_percentage) || 0,
                    duration_seconds: r.duration_seconds,
                    attempted_at: r.attempted_at,
                    degree_name: (cls as any)?.name || 'Unknown Degree',
                }
            })

            setQuizResults(mapped)
        } catch (err) {
            console.error('useUniversityProgress error:', err)
            setError((err as Error).message || 'Failed to load progress')
        } finally {
            setLoading(false)
        }
    }, [user])

    useEffect(() => { fetchData() }, [fetchData])

    // Computed values
    const degreeNames = [...new Set(quizResults.map(r => r.degree_name).filter(Boolean) as string[])]
    const totalQuizzes = quizResults.length
    const averageScore = totalQuizzes > 0
        ? Math.round(quizResults.reduce((s, r) => s + r.score_percentage, 0) / totalQuizzes)
        : 0
    const bestScore = totalQuizzes > 0
        ? Math.max(...quizResults.map(r => r.score_percentage))
        : 0

    // Extract weak areas from recent failed questions
    const weakAreas: string[] = []
    for (const result of quizResults.slice(0, 20)) {
        if (!Array.isArray(result.questions)) continue
        for (const q of result.questions) {
            if (!q.correct_yn && q.correct && !weakAreas.includes(q.correct)) {
                weakAreas.push(q.correct)
                if (weakAreas.length >= 5) break
            }
        }
        if (weakAreas.length >= 5) break
    }

    return {
        quizResults,
        degreeNames,
        totalQuizzes,
        averageScore,
        bestScore,
        weakAreas,
        loading,
        error,
        refetch: fetchData,
    }
}
